import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PlanProcedureConfig } from '@core/models/built/plan-step-config.model';
import { DataCollectorConfig } from '@core/models/data-collector-config.model';
import { emptyInterval } from '@core/models/interval.model';
import { PlanStep } from '@core/models/plan.model';
import { Procedure } from '@core/models/procedures.model';
import { WorkGroup } from '@core/models/work-unit.model';
import { ProcedureCoreService } from '@core/services/features/procedure-core.service';
import { WorkGroupsCoreService } from '@core/services/features/work-groups-core.service';
import { FlowchartMode } from '@flowchart';
import { TranslocoService } from '@ngneat/transloco';
import { filterTruthy } from '@utils';
import { BehaviorSubject, concat, of, Subscription } from 'rxjs';
import { concatMap, first, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';

import it from './i18n/plan-step-procedure-edit.it.json';


function configValidator(configGroup: AbstractControl) {
  const {
    executableByWorkGroupId,
    executableByUserIds,
    executableByDataCollectedValues
  } = configGroup.value as PlanProcedureConfig;
  if (
    !executableByWorkGroupId &&
    (!executableByUserIds || executableByUserIds.length <= 0) &&
    (!executableByDataCollectedValues || executableByDataCollectedValues.length <= 0)
  ) {
    configGroup.get('executableByWorkGroupId')?.setErrors({ required: true });
    configGroup.get('executableByUserIds')?.setErrors({ required: true });
    configGroup.get('executableByDataCollectedValues')?.setErrors({ required: true });
    return { required: true };
  }
  configGroup.get('executableByWorkGroupId')?.setErrors(null);
  configGroup.get('executableByUserIds')?.setErrors(null);
  configGroup.get('executableByDataCollectedValues')?.setErrors(null);
  return null;
};


@Component({
  templateUrl: './plan-step-procedure-edit.component.html',
  styles: [],
})
export class PlanStepProcedureEditComponent implements OnInit, OnChanges, OnDestroy {

  @Input() steps: PlanStep[] = [];
  @Input() step?: PlanStep;
  @Input() mode: FlowchartMode = 'edit';
  @Input() editForm?: FormGroup;

  procedureSearchFn = (name?: string) => this.procedureCoreSvc.getByName(name);
  procedureLabelFn = (p: Procedure) => `${p.code} - ${p.name}`;

  private step$$ = new BehaviorSubject<PlanStep | undefined>(undefined);
  step$ = this.step$$.pipe(filterTruthy());

  private steps$$ = new BehaviorSubject<PlanStep[]>([]);
  prevSteps$ = this.steps$$.pipe(
    withLatestFrom(this.step$),
    map(([steps, step]) => steps.filter(s => s.id !== step.id))
  );

  config$ = this.step$.pipe(map((step) => (step.config || {}) as PlanProcedureConfig));

  private editForm$$ = new BehaviorSubject<FormGroup | undefined>(undefined);
  editForm$ = this.editForm$$.pipe(filterTruthy());

  isStart$ = this.editForm$.pipe(
    switchMap(form => form.valueChanges.pipe(
      map(v => v.isStart),
      startWith(form.value.isStart)
    )),
  );

  currentProcedureId$ = concat(
    this.config$.pipe(map(({ procedureId }) => procedureId)),
    this.editForm$.pipe(
      concatMap(form => form.get('procedureId')?.valueChanges || of(undefined)),
      filterTruthy()
    )
  );


  workGroupSearchFn = (name?: string) => this.workGroupSvc.getList({ name });
  workGroupLabelFn = (wg: WorkGroup) => `${wg.name}`;

  private executableByDataCollectedValues$$ = new BehaviorSubject<DataCollectorConfig[]>([]);
  executableByDataCollectedValues$ = this.executableByDataCollectedValues$$.asObservable();

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private translocoSvc: TranslocoService,
    private workGroupSvc: WorkGroupsCoreService,
    private procedureCoreSvc: ProcedureCoreService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
    if (changes.steps) {
      this.steps$$.next(changes.steps.currentValue);
    }
    if (changes.editForm) {
      this.editForm$$.next(changes.editForm.currentValue);
    }
  }
  ngOnDestroy() {
    this.destroyForm();
    this.subs.unsubscribe();
  }

  initForm() {
    this.editForm$.pipe(
      first()
    ).subscribe(form => {
      const config = this.step?.config as PlanProcedureConfig;

      let configGroup = form.get('config') as FormGroup;
      if (!configGroup) {
        form.addControl('config', this.fb.group({}));
        configGroup = form.get('config') as FormGroup;
      }

      configGroup.addControl('autoAssign', this.fb.control(config?.autoAssign));
      configGroup.addControl('autoPick', this.fb.control(config?.autoPick));

      configGroup.addControl('procedureId', this.fb.control(config?.procedureId, [Validators.required]));
      configGroup.addControl('executableByWorkGroupId', this.fb.control(config?.executableByWorkGroupId));
      configGroup.addControl('executableByDataCollectedValues', this.fb.control(config?.executableByDataCollectedValues));
      configGroup.addControl('estimatedDuration', this.fb.control(config?.estimatedDuration || emptyInterval));
      configGroup.addControl('waitingTimeout', this.fb.control(config?.waitingTimeout || emptyInterval));
      configGroup.addControl('pausedTimeout', this.fb.control(config?.pausedTimeout || emptyInterval));

      configGroup.setValidators([configValidator]);
      configGroup.updateValueAndValidity();

      this.executableByDataCollectedValues$$.next(config?.executableByDataCollectedValues || []);

      if (this.mode === 'view') {
        (form.get('config') as FormGroup).disable();
      }

      this.subs.add(
        configGroup.get('autoPick')?.valueChanges.pipe(
          filterTruthy()
        ).subscribe(() => configGroup.get('autoAssign')?.setValue(true))
      );
    });
  }

  destroyForm() {
    this.editForm$.pipe(
      first()
    ).subscribe(form => {
      const configGroup = form.get('config') as FormGroup;
      if (configGroup) {
        configGroup.removeControl('autoAssign');
        configGroup.removeControl('autoPick');
        configGroup.removeControl('procedureId');
        configGroup.removeControl('executableByWorkGroupId');
        configGroup.removeControl('executableByDataCollectedValues');
        configGroup.removeControl('estimatedDuration');
        configGroup.removeControl('waitingTimeout');
        configGroup.removeControl('pausedTimeout');

        if (Object.keys(configGroup.controls).length <= 0) {
          form.removeControl('config');
        }
      }
    });
  }

  addExecutableByDataCollectedValues(value: DataCollectorConfig) {
    this.editForm$.pipe(
      first()
    ).subscribe(form => {
      const executableByDataCollectedValues: DataCollectorConfig[] = [
        ...this.executableByDataCollectedValues$$.getValue(),
        value
      ];
      this.executableByDataCollectedValues$$.next(executableByDataCollectedValues);
      const ctl = (form.get('config.executableByDataCollectedValues') as FormControl);
      ctl.setValue(executableByDataCollectedValues);
      ctl.markAsTouched();
    });
  }

  removeExecutableByDataCollectedValues(index: number) {
    this.editForm$.pipe(
      first()
    ).subscribe(form => {
      const executableByDataCollectedValues: DataCollectorConfig[] = this.executableByDataCollectedValues$$.getValue()
        .filter((v, i) => i !== index);
      this.executableByDataCollectedValues$$.next(executableByDataCollectedValues);
      const ctl = (form.get('config.executableByDataCollectedValues') as FormControl);
      ctl.setValue(executableByDataCollectedValues);
      ctl.markAsTouched();
    });
  }
}
